/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 *
 * In production, scripts/prerender.ts writes prerendered HTML into
 * `<div id="root">` for crawler-visible content. When that markup is
 * present we hydrate it; otherwise we mount the SPA into an empty root
 * (dev mode / non-prerendered paths fall through to CSR).
 */

import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { App } from "./App";
import { router } from "./router";
import { bootstrapAnalytics } from "./lib/track";

bootstrapAnalytics();

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  // HMR only runs in dev, where the root is empty — always create.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else if (elem.hasChildNodes()) {
  // Prerendered markup is present — hydrate so React reuses the DOM nodes
  // instead of throwing them away. `hasChildNodes()` is the cheapest reliable
  // signal for SSR content (avoids the React 18+ `data-reactroot` non-attr).
  // The router's matches must be resolved BEFORE hydrateRoot: an unloaded
  // router renders the route-level Suspense fallback (null) on the first
  // pass, which React reports as a mismatch against the server markup and
  // regenerates the whole tree client-side.
  await router.load();
  hydrateRoot(elem, app);
} else {
  // Empty shell (CSR fallback for non-prerendered routes, e.g. auth pages).
  createRoot(elem).render(app);
}
